import React, { useState, useEffect, useCallback } from 'react';
import {
  Package,
  Search,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Clock,
  AlertCircle,
  Loader2,
  RefreshCw,
  ExternalLink,
  Play,
  Eye,
  Filter,
  X,
  CircleDot,
  Zap,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axiosInstance from '../utils/axiosInstance';
import ShippyLoader from '../components/ShippyLoader';

const PAGE_SIZE = 20;

const OutPacking = () => {
  const [allOrders, setAllOrders] = useState([]); // Full filtered list (across all pages)
  const [orders, setOrders] = useState([]); // Current page slice
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('search') || '';
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);
  const [simulatingId, setSimulatingId] = useState(null);
  const [filterNotGenerated, setFilterNotGenerated] = useState(false);
  // Execution queue: array of { id, name, status: 'pending'|'processing'|'done'|'error', error? }
  const [queue, setQueue] = useState([]);
  // Force pack modal
  const [forcePackOpen, setForcePackOpen] = useState(false);
  const [forcePackLabel, setForcePackLabel] = useState('');
  const [forcePackLoading, setForcePackLoading] = useState(false);
  const [forcePackResults, setForcePackResults] = useState(null);
  const [forcePackError, setForcePackError] = useState('');

  // Fetch delivery orders from the API
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('/api/odoo/delivery-orders/', {
        params: {
          search: search || undefined,
          include_packing_status: 'true',
        },
      });
      const data = response.data;
      let filtered = data.delivery_orders || [];

      // Client-side search filter (backend doesn't support search param)
      if (search) {
        const q = search.toLowerCase();
        filtered = filtered.filter(
          (o) =>
            (o.name || '').toLowerCase().includes(q) ||
            (o.partner || '').toLowerCase().includes(q) ||
            (o.origin || '').toLowerCase().includes(q)
        );
      }

      // Filter to show only not-generated orders
      if (filterNotGenerated) {
        filtered = filtered.filter((o) => !o.packing_generated);
      }

      setAllOrders(filtered);
      setTotalCount(filtered.length);
      setTotalPages(Math.ceil(filtered.length / PAGE_SIZE));

      // Client-side pagination
      const start = (page - 1) * PAGE_SIZE;
      setOrders(filtered.slice(start, start + PAGE_SIZE));
    } catch (err) {
      setError('Failed to load delivery orders. Please try again.');
      setAllOrders([]);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [search, page, filterNotGenerated]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [search, filterNotGenerated]);

  // Toggle selection for a single order
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Toggle select all on current page
  const toggleSelectAll = () => {
    const packableIds = orders
      .filter((o) => !o.packing_generated)
      .map((o) => o.id);

    const allSelected = packableIds.length > 0 && packableIds.every((id) => selected.has(id));

    if (allSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        packableIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        packableIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  // Select ALL packable orders across all pages
  const selectAllOrders = () => {
    const packableIds = allOrders
      .filter((o) => !o.packing_generated)
      .map((o) => o.id);
    setSelected(new Set(packableIds));
  };

  const totalPackable = allOrders.filter((o) => !o.packing_generated).length;

  // Generate packing instructions for selected orders and push to Odoo
  const handleGenerate = async () => {
    if (selected.size === 0) return;
    setGenerating(true);
    setGenerateResult(null);
    setError('');

    // Build queue from selected orders (search in allOrders to support cross-page selection)
    const selectedOrders = allOrders.filter((o) => selected.has(o.id));
    const initialQueue = selectedOrders.map((o) => ({
      id: o.id,
      name: o.name,
      partner: o.partner,
      status: 'pending',
    }));
    setQueue(initialQueue);

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < initialQueue.length; i++) {
      const item = initialQueue[i];
      // Mark current as processing
      setQueue((prev) =>
        prev.map((q) => (q.id === item.id ? { ...q, status: 'processing' } : q))
      );

      try {
        await axiosInstance.post(`/api/odoo/packing-instructions/${item.id}/`);
        successCount++;
        setQueue((prev) =>
          prev.map((q) => (q.id === item.id ? { ...q, status: 'done' } : q))
        );
      } catch (err) {
        errorCount++;
        setQueue((prev) =>
          prev.map((q) =>
            q.id === item.id
              ? { ...q, status: 'error', error: err.response?.data?.message || 'Failed' }
              : q
          )
        );
      }
    }

    setGenerateResult({ generated_count: successCount, error_count: errorCount });
    setSelected(new Set());
    setGenerating(false);
    fetchOrders();
  };

  // Generate packing for a single order and push to Odoo
  const handleSimulateSingle = async (pickingId) => {
    setSimulatingId(pickingId);
    setError('');
    try {
      await axiosInstance.post(`/api/odoo/packing-instructions/${pickingId}/`);
      setGenerateResult({ generated_count: 1, error_count: 0 });
      fetchOrders();
    } catch {
      setError('Failed to generate packing for this order.');
    } finally {
      setSimulatingId(null);
    }
  };

  // Force pack: look up an OUT label and simulate
  const handleForcePack = async () => {
    if (!forcePackLabel.trim()) return;
    setForcePackLoading(true);
    setForcePackError('');
    setForcePackResults(null);
    try {
      // Look up the picking by name
      const lookup = await axiosInstance.post('/api/odoo/delivery-orders/lookup/', { name: forcePackLabel.trim() });
      const pickings = lookup.data.pickings || [];
      if (pickings.length === 0) {
        setForcePackError(`No delivery order found matching "${forcePackLabel.trim()}"`);
        setForcePackLoading(false);
        return;
      }
      setForcePackResults(pickings);
      setForcePackLoading(false);
    } catch (err) {
      setForcePackError(err.response?.data?.message || 'Failed to look up delivery order');
      setForcePackLoading(false);
    }
  };

  const handleForcePackGenerate = async (pickingId) => {
    setForcePackLoading(true);
    setForcePackError('');
    try {
      await axiosInstance.post(`/api/odoo/packing-instructions/${pickingId}/`);
      setForcePackOpen(false);
      setForcePackLabel('');
      setForcePackResults(null);
      setGenerateResult({ generated_count: 1, error_count: 0 });
      fetchOrders();
    } catch (err) {
      setForcePackError(err.response?.data?.message || 'Failed to generate packing instructions');
    } finally {
      setForcePackLoading(false);
    }
  };

  // Render packing status badge
  const renderPackingStatus = (order) => {
    if (order.packing_generated) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <CheckCircle2 className="h-3 w-3" />
          Generated
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
        Not generated
      </span>
    );
  };

  // Render delivery order state badge
  const renderState = (state, stateDisplay) => {
    const styles = {
      assigned: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      confirmed: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
      done: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
      cancel: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      waiting: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    };
    const label = stateDisplay || (state ? state.charAt(0).toUpperCase() + state.slice(1) : 'Unknown');
    return (
      <span
        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
          styles[state] || styles.draft
        }`}
      >
        {label}
      </span>
    );
  };

  const packableOnPage = orders.filter((o) => !o.packing_generated);
  const allPageSelected =
    packableOnPage.length > 0 && packableOnPage.every((o) => selected.has(o.id));

  return (
    <div className="px-6 py-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <Package className="h-6 w-6" />
            OUT Packing
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Generate packing instructions for outgoing delivery orders.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setForcePackOpen(true); setForcePackLabel(''); setForcePackResults(null); setForcePackError(''); }}
          >
            <Zap className="h-4 w-4 mr-1.5" />
            Force Pack
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchOrders}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {totalPackable > 0 && selected.size < totalPackable && (
            <Button
              variant="outline"
              size="sm"
              onClick={selectAllOrders}
              disabled={generating}
            >
              <Package className="h-4 w-4 mr-1.5" />
              Pack All ({totalPackable})
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleGenerate}
            disabled={selected.size === 0 || generating}
          >
            {generating ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Package className="h-4 w-4 mr-1.5" />
            )}
            {generating
              ? 'Generating...'
              : `Generate Packing (${selected.size})`}
          </Button>
        </div>
      </div>

      {/* Success message */}
      {generateResult && (
        <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            <span className="font-medium">
              Packing instructions generated successfully.
            </span>
          </div>
          <p className="mt-1 ml-6">
            {generateResult.generated_count} order(s) processed.
            {generateResult.error_count > 0 && ` ${generateResult.error_count} failed.`}
          </p>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        </div>
      )}

      {/* Execution Queue */}
      {queue.length > 0 && (
        <div className="mt-4 rounded-xl border border-border/70 surface-panel shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/50">
            <div className="flex items-center gap-2">
              <CircleDot className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">
                Execution Queue
              </span>
              <span className="text-xs text-muted-foreground">
                {queue.filter((q) => q.status === 'done').length}/{queue.length} completed
              </span>
            </div>
            {!generating && (
              <button
                onClick={() => setQueue([])}
                className="rounded p-1 hover:bg-muted text-muted-foreground"
                title="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {/* Progress bar */}
          <div className="h-1.5 bg-muted">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{
                width: `${(queue.filter((q) => q.status === 'done' || q.status === 'error').length / queue.length) * 100}%`,
              }}
            />
          </div>
          <div className="max-h-48 overflow-y-auto divide-y divide-border">
            {queue.map((item) => (
              <div
                key={item.id}
                className={`flex items-center gap-3 px-4 py-2 text-sm ${
                  item.status === 'processing' ? 'bg-primary/5' : ''
                }`}
              >
                <div className="shrink-0">
                  {item.status === 'pending' && (
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                  {item.status === 'processing' && (
                    <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
                  )}
                  {item.status === 'done' && (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  )}
                  {item.status === 'error' && (
                    <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                  )}
                </div>
                <span className="font-medium text-foreground min-w-[140px]">
                  {item.name}
                </span>
                <span className="text-xs text-muted-foreground truncate">
                  {item.partner}
                </span>
                <span className="ml-auto text-xs shrink-0">
                  {item.status === 'pending' && (
                    <span className="text-muted-foreground">Waiting</span>
                  )}
                  {item.status === 'processing' && (
                    <span className="text-primary font-medium">Processing...</span>
                  )}
                  {item.status === 'done' && (
                    <span className="text-emerald-600">Done</span>
                  )}
                  {item.status === 'error' && (
                    <span className="text-red-500" title={item.error}>Failed</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search bar + filter */}
      <div className="mt-6 flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search orders by name or partner..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          variant={filterNotGenerated ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterNotGenerated((v) => !v)}
        >
          <Filter className="h-4 w-4 mr-1.5" />
          Not generated
        </Button>
        <p className="text-sm text-muted-foreground">
          {totalCount} order{totalCount !== 1 ? 's' : ''} found
        </p>
      </div>

      {/* Cross-page selection banner */}
      {selected.size > 0 && selected.size > orders.filter(o => selected.has(o.id)).length && (
        <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800 px-4 py-2 text-sm text-blue-800 dark:text-blue-200 flex items-center justify-between">
          <span>
            <strong>{selected.size}</strong> orders selected across all pages.
          </span>
          <button onClick={() => setSelected(new Set())} className="text-xs underline hover:no-underline">
            Clear selection
          </button>
        </div>
      )}

      {/* Table */}
      <div className="mt-4 rounded-xl border border-border/70 surface-panel shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="w-12 px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={toggleSelectAll}
                    disabled={packableOnPage.length === 0}
                    className="h-4 w-4 rounded border-input accent-primary"
                  />
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  Order
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  Origin
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  Partner
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  Scheduled
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  State
                </th>
                <th className="px-4 py-3 text-center font-medium text-muted-foreground">
                  Moves
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                  Packing
                </th>
                <th className="px-4 py-3 text-center font-medium text-muted-foreground">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <ShippyLoader size={48} message="Loading orders..." />
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Package className="h-8 w-8" />
                      <span>No delivery orders found.</span>
                    </div>
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  const isDone = order.packing_generated;
                  return (
                    <tr
                      key={order.id}
                      className={`border-b border-border last:border-0 transition-colors ${
                        selected.has(order.id)
                          ? 'bg-primary/5'
                          : 'hover:bg-muted/30'
                      } ${isDone ? 'opacity-60' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(order.id)}
                          onChange={() => toggleSelect(order.id)}
                          disabled={isDone}
                          className="h-4 w-4 rounded border-input accent-primary"
                        />
                      </td>
                      <td className="px-4 py-3 font-medium text-foreground">
                        {order.name}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {order.origin || '-'}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {order.partner || '-'}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">
                        {order.scheduled_date ? new Date(order.scheduled_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '-'}
                      </td>
                      <td className="px-4 py-3">{renderState(order.state, order.state_display)}</td>
                      <td className="px-4 py-3 text-center text-muted-foreground">
                        {order.move_count ?? '-'}
                      </td>
                      <td className="px-4 py-3">
                        {renderPackingStatus(order)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          {!isDone && (
                            <button
                              onClick={() => handleSimulateSingle(order.id)}
                              disabled={simulatingId === order.id}
                              className="rounded p-1.5 hover:bg-primary/10 text-primary"
                              title="Generate packing instructions"
                            >
                              {simulatingId === order.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Play className="h-4 w-4" />
                              )}
                            </button>
                          )}
                          {isDone && (
                            <>
                              <button
                                onClick={() => handleSimulateSingle(order.id)}
                                disabled={simulatingId === order.id}
                                className="rounded p-1.5 hover:bg-orange-100 dark:hover:bg-orange-900/30 text-orange-500"
                                title="Re-run packing analysis"
                              >
                                {simulatingId === order.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-4 w-4" />
                                )}
                              </button>
                              <button
                                onClick={() => window.open(`/api/odoo/packing-instructions/${order.id}/`, '_blank')}
                                className="rounded p-1.5 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-emerald-600"
                                title="View packing instructions"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {order.odoo_url && (
                            <a
                              href={order.odoo_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rounded p-1.5 hover:bg-muted text-muted-foreground hover:text-foreground"
                              title="Open in Odoo"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-3">
            <p className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Force Pack Modal */}
      {forcePackOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-500" />
                Force Pack
              </h3>
              <button onClick={() => setForcePackOpen(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Enter the OUT label to generate packing instructions for an order not in the list.
              </p>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g. LAMP/OUT/201691"
                  value={forcePackLabel}
                  onChange={(e) => setForcePackLabel(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleForcePack()}
                  className="flex-1"
                  autoFocus
                />
                <Button
                  size="sm"
                  onClick={handleForcePack}
                  disabled={!forcePackLabel.trim() || forcePackLoading}
                >
                  {forcePackLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>

              {forcePackError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {forcePackError}
                </div>
              )}

              {forcePackResults && forcePackResults.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">
                    {forcePackResults.length} result{forcePackResults.length !== 1 ? 's' : ''} found — click to generate packing:
                  </p>
                  <div className="max-h-60 overflow-y-auto space-y-1.5">
                    {forcePackResults.map((p) => (
                      <div key={p.id} className="rounded-lg border border-border overflow-hidden">
                        <button
                          onClick={() => handleForcePackGenerate(p.id)}
                          disabled={forcePackLoading}
                          className="w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors disabled:opacity-50"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-sm font-semibold">{p.name}</span>
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                              p.state === 'assigned' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                              p.state === 'done' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                              'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                            }`}>
                              {p.state_display}
                            </span>
                          </div>
                          <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                            {p.partner && <span>{p.partner}</span>}
                            {p.origin && <span>Origin: {p.origin}</span>}
                            <span>{p.move_count} move{p.move_count !== 1 ? 's' : ''}</span>
                          </div>
                        </button>
                        {p.moves && p.moves.length > 0 && (
                          <div className="border-t border-border bg-muted/20 px-3 py-2">
                            <p className="text-[10px] font-semibold text-muted-foreground mb-1 uppercase tracking-wide">Stock Moves</p>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-muted-foreground">
                                  <th className="text-left font-medium pb-0.5">Product</th>
                                  <th className="text-right font-medium pb-0.5 w-14">Qty</th>
                                  <th className="text-left font-medium pb-0.5 pl-2">Dims</th>
                                  <th className="text-right font-medium pb-0.5 w-14">Boxes</th>
                                  <th className="text-right font-medium pb-0.5 w-16">State</th>
                                </tr>
                              </thead>
                              <tbody>
                                {p.moves.map((m) => (
                                  <tr key={m.id}>
                                    <td className="py-0.5 truncate max-w-[160px]" title={m.name}>{m.name}</td>
                                    <td className="text-right py-0.5 font-mono">{m.demand}</td>
                                    <td className="py-0.5 pl-2 text-[10px] text-muted-foreground" title={m.dims_source}>{m.dims}</td>
                                    <td className="text-right py-0.5 font-mono">
                                      {m.box_count > 1 ? <span className="text-amber-600 font-semibold">{m.box_count}</span> : m.box_count}
                                    </td>
                                    <td className="text-right py-0.5">
                                      <span className={`inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-medium ${
                                        m.state === 'done' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                        m.state === 'cancel' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                        'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                      }`}>{m.state}</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutPacking;
